import { useMutation, useQueryClient } from '@tanstack/react-query';
import { generateGuidelinesFromFiles } from '../api/guidelines.api';
import { useCurrentWorkspaceId } from '@/entities/workspace';
import { notificationService } from '@/lib/services/notification.service';
import type { GuidelineResponseDto } from '@/lib/api/guidelines-client';
import { useTranslation } from 'react-i18next';

export interface GenerateGuidelinesParams {
  files: File[];
  folderId?: string;
}

export const useGenerateGuidelinesFromFiles = () => {
  const { t } = useTranslation('knowledge');
  const queryClient = useQueryClient();
  const workspaceId = useCurrentWorkspaceId();

  return useMutation<GuidelineResponseDto[], Error, GenerateGuidelinesParams>({
    mutationFn: async ({ files, folderId }) => {
      if (!workspaceId) throw new Error(t('guidelines.workspaceIdIsRequired'));

      // Generate and create guidelines from files in a single API call
      const result = await generateGuidelinesFromFiles(files, workspaceId, { folderId });
      if (!result?.guidelines.length) {
        throw new Error(t('guidelines.useGenerateGuidelinesFromFiles.noGuidelinesGenerated'));
      }

      return result.guidelines;
    },
    onSuccess: (data, variables) => {
      if (variables.folderId) {
        void queryClient.invalidateQueries({
          queryKey: ['guidelines', 'folder', variables.folderId],
        });
      }
      void queryClient.invalidateQueries({ queryKey: ['folders', workspaceId] });
      notificationService.showSuccess(
        t('guidelines.useGenerateGuidelinesFromFiles.guidelinesImported', {
          count: data.length,
        }),
        { anchorOrigin: { vertical: 'bottom', horizontal: 'center' } },
      );
    },
    onError: (error) => {
      notificationService.showError(
        error.message || t('guidelines.useGenerateGuidelinesFromFiles.failedToImportGuidelines'),
      );
    },
  });
};
